/**
 * src/lib/icons.jsx — GENERADO por scripts/gen-icons.mjs (no editar a mano).
 *
 * SOA 10 PRO: todo el OS usa Material Symbols Rounded (24px barra / 20px tabla, FILL 1 activo).
 * Este shim exporta los MISMOS nombres que lucide-react para no tocar 180 archivos: cada ícono
 * renderiza el ligature de Material Symbols; el tamaño sale de h-N / w-N / size-N del className
 * (o de la prop size). Los íconos sin equivalente (logos de redes) se re-exportan de lucide.
 */
import { forwardRef } from "react";
import {
  Facebook,
  Github,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
} from "lucide-react";

export { Facebook, Github, Instagram, Linkedin, Twitter, Youtube };

const RE_PX = /(?:^|\s)(?:size|h|w)-(?:\[(\d+(?:\.\d+)?)px\]|(\d+(?:\.\d+)?))(?=\s|$)/;
function tamano(className, size) {
  if (size) return Number(size);
  const m = RE_PX.exec(className || "");
  if (!m) return null;                 // sin tamaño explícito → manda el CSS del contenedor (.msr = 24px)
  return m[1] ? Number(m[1]) : Number(m[2]) * 4;
}

/** Ícono genérico por nombre de Material Symbols. */
export const Icon = forwardRef(function Icon({ name, className = "", size, fill, style, strokeWidth, absoluteStrokeWidth, color, ...rest }, ref) {
  const px = tamano(className, size);
  const lleno = fill ?? /(?:^|\s)fill-(?!none)/.test(className);
  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={`msr ${className}`}
      style={{ ...(px ? { fontSize: px } : null), ...(lleno ? { fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" } : null), ...(color ? { color } : null), ...style }}
      {...rest}
    >
      {name}
    </span>
  );
});

const mk = (name, display) => {
  const C = forwardRef((props, ref) => <Icon ref={ref} name={name} {...props} />);
  C.displayName = display;
  return C;
};

export const Activity = mk("monitoring", "Activity");
export const AlertCircle = mk("error", "AlertCircle");
export const AlertTriangle = mk("warning", "AlertTriangle");
export const Archive = mk("archive", "Archive");
export const ArchiveRestore = mk("unarchive", "ArchiveRestore");
export const ArrowLeft = mk("arrow_back", "ArrowLeft");
export const ArrowRight = mk("arrow_forward", "ArrowRight");
export const ArrowRightLeft = mk("swap_horiz", "ArrowRightLeft");
export const ArrowUpCircle = mk("arrow_circle_up", "ArrowUpCircle");
export const ArrowUpDown = mk("swap_vert", "ArrowUpDown");
export const AtSign = mk("alternate_email", "AtSign");
export const Award = mk("award_star", "Award");
export const Ban = mk("block", "Ban");
export const Banknote = mk("payments", "Banknote");
export const BarChart3 = mk("bar_chart", "BarChart3");
export const Beaker = mk("science", "Beaker");
export const Bell = mk("notifications", "Bell");
export const BellOff = mk("notifications_off", "BellOff");
export const BookOpen = mk("menu_book", "BookOpen");
export const Bot = mk("smart_toy", "Bot");
export const Brain = mk("psychology", "Brain");
export const BrainCircuit = mk("psychology", "BrainCircuit");
export const Briefcase = mk("work", "Briefcase");
export const Building2 = mk("apartment", "Building2");
export const Cake = mk("cake", "Cake");
export const Calculator = mk("calculate", "Calculator");
export const Calendar = mk("calendar_today", "Calendar");
export const CalendarClock = mk("event", "CalendarClock");
export const CalendarDays = mk("calendar_month", "CalendarDays");
export const Camera = mk("photo_camera", "Camera");
export const Check = mk("check", "Check");
export const CheckCheck = mk("done_all", "CheckCheck");
export const CheckCircle2 = mk("check_circle", "CheckCircle2");
export const CheckSquare = mk("check_box", "CheckSquare");
export const ChefHat = mk("restaurant", "ChefHat");
export const ChevronDown = mk("expand_more", "ChevronDown");
export const ChevronLeft = mk("chevron_left", "ChevronLeft");
export const ChevronRight = mk("chevron_right", "ChevronRight");
export const ChevronUp = mk("expand_less", "ChevronUp");
export const Circle = mk("circle", "Circle");
export const CircleDollarSign = mk("paid", "CircleDollarSign");
export const ClipboardCheck = mk("assignment_turned_in", "ClipboardCheck");
export const Clock = mk("schedule", "Clock");
export const Cloud = mk("cloud", "Cloud");
export const Coffee = mk("coffee", "Coffee");
export const Cog = mk("settings", "Cog");
export const Command = mk("keyboard_command_key", "Command");
export const Copy = mk("content_copy", "Copy");
export const Cpu = mk("memory", "Cpu");
export const CreditCard = mk("credit_card", "CreditCard");
export const Crown = mk("workspace_premium", "Crown");
export const Dice5 = mk("casino", "Dice5");
export const DollarSign = mk("attach_money", "DollarSign");
export const Download = mk("download", "Download");
export const Edit2 = mk("edit", "Edit2");
export const Edit3 = mk("edit_note", "Edit3");
export const ExternalLink = mk("open_in_new", "ExternalLink");
export const Eye = mk("visibility", "Eye");
export const File = mk("draft", "File");
export const FileBarChart = mk("analytics", "FileBarChart");
export const FileBox = mk("inventory", "FileBox");
export const FileCheck = mk("task", "FileCheck");
export const FileEdit = mk("edit_document", "FileEdit");
export const FileJson = mk("data_object", "FileJson");
export const FileSpreadsheet = mk("table_chart", "FileSpreadsheet");
export const FileText = mk("description", "FileText");
export const Film = mk("movie", "Film");
export const Filter = mk("filter_alt", "Filter");
export const Flag = mk("flag", "Flag");
export const Gavel = mk("gavel", "Gavel");
export const Flame = mk("local_fire_department", "Flame");
export const FolderOpen = mk("folder_open", "FolderOpen");
export const FolderPlus = mk("create_new_folder", "FolderPlus");
export const GanttChartSquare = mk("view_timeline", "GanttChartSquare");
export const Gauge = mk("speed", "Gauge");
export const Gem = mk("diamond", "Gem");
export const GitBranch = mk("account_tree", "GitBranch");
export const Globe = mk("language", "Globe");
export const GraduationCap = mk("school", "GraduationCap");
export const GripVertical = mk("drag_indicator", "GripVertical");
export const Handshake = mk("handshake", "Handshake");
export const Hash = mk("tag", "Hash");
export const Headphones = mk("headphones", "Headphones");
export const Heart = mk("favorite", "Heart");
export const History = mk("history", "History");
export const Home = mk("home", "Home");
export const IdCard = mk("badge", "IdCard");
export const Image = mk("image", "Image");
export const Inbox = mk("inbox", "Inbox");
export const Info = mk("info", "Info");
export const Kanban = mk("view_kanban", "Kanban");
export const Layers = mk("layers", "Layers");
export const LayoutDashboard = mk("dashboard", "LayoutDashboard");
export const LayoutGrid = mk("grid_view", "LayoutGrid");
export const Lightbulb = mk("lightbulb", "Lightbulb");
export const Link = mk("link", "Link");
export const Link2 = mk("link", "Link2");
export const List = mk("list", "List");
export const ListChecks = mk("checklist", "ListChecks");
export const ListFilter = mk("filter_list", "ListFilter");
export const ListTodo = mk("checklist", "ListTodo");
export const Loader2 = mk("progress_activity", "Loader2");
export const Lock = mk("lock", "Lock");
export const LogOut = mk("logout", "LogOut");
export const Mail = mk("mail", "Mail");
export const MapPin = mk("location_on", "MapPin");
export const Maximize2 = mk("open_in_full", "Maximize2");
export const Medal = mk("military_tech", "Medal");
export const Megaphone = mk("campaign", "Megaphone");
export const Menu = mk("menu", "Menu");
export const MessageCircle = mk("chat_bubble", "MessageCircle");
export const MessageSquare = mk("chat_bubble", "MessageSquare");
export const Mic = mk("mic", "Mic");
export const Minus = mk("remove", "Minus");
export const Monitor = mk("desktop_windows", "Monitor");
export const MoreHorizontal = mk("more_horiz", "MoreHorizontal");
export const MoreVertical = mk("more_vert", "MoreVertical");
export const Music = mk("music_note", "Music");
export const Music2 = mk("music_note", "Music2");
export const Package = mk("inventory_2", "Package");
export const Palette = mk("palette", "Palette");
export const Paperclip = mk("attach_file", "Paperclip");
export const Pause = mk("pause", "Pause");
export const Pencil = mk("edit", "Pencil");
export const Percent = mk("percent", "Percent");
export const Phone = mk("call", "Phone");
export const PhoneOff = mk("phone_disabled", "PhoneOff");
export const PieChart = mk("pie_chart", "PieChart");
export const Pin = mk("keep", "Pin");
export const Plane = mk("flight", "Plane");
export const Play = mk("play_arrow", "Play");
export const PlayCircle = mk("play_circle", "PlayCircle");
export const Plug = mk("power", "Plug");
export const Plus = mk("add", "Plus");
export const Presentation = mk("slideshow", "Presentation");
export const Receipt = mk("receipt_long", "Receipt");
export const RefreshCw = mk("refresh", "RefreshCw");
export const Repeat = mk("repeat", "Repeat");
export const Reply = mk("reply", "Reply");
export const RotateCcw = mk("rotate_left", "RotateCcw");
export const Route = mk("route", "Route");
export const Save = mk("save", "Save");
export const Search = mk("search", "Search");
export const Send = mk("send", "Send");
export const Settings = mk("settings", "Settings");
export const Settings2 = mk("tune", "Settings2");
export const Share2 = mk("share", "Share2");
export const Shield = mk("shield", "Shield");
export const ShieldAlert = mk("gpp_maybe", "ShieldAlert");
export const ShieldCheck = mk("verified_user", "ShieldCheck");
export const Smartphone = mk("smartphone", "Smartphone");
export const Smile = mk("mood", "Smile");
export const SmilePlus = mk("add_reaction", "SmilePlus");
export const Sparkles = mk("auto_awesome", "Sparkles");
export const Square = mk("check_box_outline_blank", "Square");
export const Star = mk("star", "Star");
export const StopCircle = mk("stop_circle", "StopCircle");
export const Sun = mk("light_mode", "Sun");
export const Table = mk("table", "Table");
export const Table2 = mk("table_rows", "Table2");
export const Tag = mk("sell", "Tag");
export const Target = mk("target", "Target");
export const ThumbsDown = mk("thumb_down", "ThumbsDown");
export const ThumbsUp = mk("thumb_up", "ThumbsUp");
export const Ticket = mk("confirmation_number", "Ticket");
export const Timer = mk("timer", "Timer");
export const Trash2 = mk("delete", "Trash2");
export const TrendingDown = mk("trending_down", "TrendingDown");
export const TrendingUp = mk("trending_up", "TrendingUp");
export const Trophy = mk("emoji_events", "Trophy");
export const Undo2 = mk("undo", "Undo2");
export const Unlock = mk("lock_open", "Unlock");
export const Upload = mk("upload", "Upload");
export const User = mk("person", "User");
export const UserCheck = mk("how_to_reg", "UserCheck");
export const UserCog = mk("manage_accounts", "UserCog");
export const UserMinus = mk("person_remove", "UserMinus");
export const UserPlus = mk("person_add", "UserPlus");
export const Users = mk("groups", "Users");
export const UserSquare2 = mk("account_box", "UserSquare2");
export const UserX = mk("person_off", "UserX");
export const Video = mk("videocam", "Video");
export const Volume2 = mk("volume_up", "Volume2");
export const VolumeX = mk("volume_off", "VolumeX");
export const Wallet = mk("account_balance_wallet", "Wallet");
export const Wand2 = mk("auto_fix_high", "Wand2");
export const Wrench = mk("build", "Wrench");
export const X = mk("close", "X");
export const XCircle = mk("cancel", "XCircle");
export const Zap = mk("bolt", "Zap");
